import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material'

export let HeroCarouselSkeleton = () => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      height: { xs: 400, md: 600 },
      borderRadius: 2,
      overflow: 'hidden',
      mb: 4,
    }}
  >
    <Skeleton
      variant="rectangular"
      width="100%"
      height="100%"
      animation="wave"
      sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        right: 16,
      }}
    >
      <Skeleton
        variant="text"
        width="60%"
        height={48}
        sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 1 }}
      />
      <Skeleton
        variant="text"
        width="40%"
        height={24}
        sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
      />
    </Box>
    {/* Slide indicators */}
    <Box
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        display: 'flex',
        gap: 1,
      }}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.4)',
          }}
        />
      ))}
    </Box>
  </Box>
)

export let SelfPacedCoursePageSkeleton = () => (
  <Box sx={{ px: 3, py: 4 }}>
    <Skeleton
      variant="text"
      width="40%"
      height={48}
      sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
    />
    <Box className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          animation="wave"
          sx={{
            aspectRatio: '16/9',
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.1)',
          }}
        />
      ))}
    </Box>
  </Box>
)

export let CourseCardSkeleton = () => (
  <Box sx={{ minWidth: 280 }}>
    <Card
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        '& .MuiCardContent-root': {
          bgcolor: '#000000',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16/9',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#000000',
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
        />
      </Box>
      <CardContent sx={{ pt: 1.5, pb: 1.5 }}>
        <Skeleton
          variant="text"
          width="80%"
          height={20}
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 0.5 }}
        />
        <Skeleton
          variant="text"
          width="60%"
          height={16}
          sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
        />
      </CardContent>
    </Card>
  </Box>
)

export let SeriesCardSkeleton = () => (
  <Box sx={{ minWidth: 280 }}>
    <Card
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        '& .MuiCardContent-root': {
          bgcolor: '#000000',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '2/3',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#000000',
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
        />
      </Box>
      <CardContent sx={{ pt: 1.5, pb: 1.5 }}>
        <Skeleton
          variant="text"
          width="80%"
          height={20}
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 0.5 }}
        />
        <Skeleton
          variant="text"
          width="60%"
          height={16}
          sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
        />
      </CardContent>
    </Card>
  </Box>
)

export let HorizontalCarouselSkeleton = ({
  title,
  isSeries = false,
}: {
  title: string
  isSeries?: boolean
}) => (
  <Box sx={{ mb: 4 }}>
    <Box
      sx={{
        px: 3,
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" sx={{ color: 'white' }}>
        {title}
      </Typography>
    </Box>
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        px: 3,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
      }}
    >
      {Array.from({ length: 4 }).map((_, index) =>
        isSeries ? (
          <SeriesCardSkeleton key={index} />
        ) : (
          <CourseCardSkeleton key={index} />
        ),
      )}
    </Box>
  </Box>
)

export let ActionItemCardSkeleton = () => (
  <Box sx={{ minWidth: 320 }}>
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: '#000000',
      }}
    >
      <Skeleton
        variant="rectangular"
        width="100%"
        height="100%"
        animation="wave"
        sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
      />
      <Skeleton
        variant="rounded"
        width={90}
        height={24}
        animation="wave"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          bgcolor: 'rgba(255,255,255,0.2)',
        }}
      />
    </Box>
    <Skeleton
      variant="text"
      width="80%"
      height={24}
      sx={{ bgcolor: 'rgba(255,255,255,0.2)', mt: 1.5 }}
    />
    <Skeleton
      variant="text"
      width="60%"
      height={18}
      sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
    />
  </Box>
)

export let ActionItemsCarouselSkeleton = ({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string
}) => (
  <Box sx={{ mb: 4 }}>
    <span className="sr-only" role="status" aria-live="polite">
      Loading {title}…
    </span>
    <Box sx={{ px: 3, mb: 2 }}>
      <Typography variant="h5" sx={{ color: 'white' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        px: 3,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
      }}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <ActionItemCardSkeleton key={index} />
      ))}
    </Box>
  </Box>
)

export let ContinueWatchingPageSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gap: 3,
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(4, 1fr)',
      },
    }}
  >
    <span className="sr-only" role="status" aria-live="polite">
      Loading courses…
    </span>
    {Array.from({ length: 8 }).map((_, index) => (
      <ActionItemCardSkeleton key={index} />
    ))}
  </Box>
)

export let QuickAccessBannerSkeleton = () => (
  <Box sx={{ mb: 6, px: 3 }}>
    <Box className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Box key={index}>
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
            }}
            animation="wave"
          />
        </Box>
      ))}
    </Box>
  </Box>
)
